import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import BackButton from 'src/features/navigation/components/BackButton';
import { useGetLinkedEntities } from 'src/features/pricingPlan/api/linkedEntities/useGetLinkedEntities';
import { useListPricingPlanTiers } from 'src/features/pricingPlan/api/tiers/useListPricingPlanTiers';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { useSimulatePricingPlan } from 'src/features/pricingPlan/api/useSimulatePricingPlan';
import PricingPlanBadge from 'src/features/pricingPlan/components/PricingPlanBadge';
import PricingSimulationTable from 'src/features/pricingPlan/components/PricingSimulationTable';
import { pricingCalculationBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import {
    PricingSimulationFormSchemaValues,
    pricingSimulationFormSchema,
} from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { CALCULATION_TYPE, PRICING_SIMULATION_CONTEXT } from 'src/features/pricingPlan/utils/types';
import { VISITOR_TYPE } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import DatePickerWithTime from 'src/registry/ui/DatePickerWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { SingleAttributeError, useFormatCustomError } from 'src/utils/errors';

// The period the screen offers by default - the end follows the start by a week
const DEFAULT_PERIOD_DAYS = 7;
const MINUTES_IN_DAY = 24 * 60;
// A breakdown is refused above a thousand rows and a row is one tick, so a tariff with a short tick is offered a
// shorter period instead of one the server would turn down. The margin is for the rows a tick does not produce -
// free time, the stretches no tariff applies for.
const MAX_OFFERED_TICKS = 900;

// Only an object resolves its tariff through assignments, so the type of the visitor is sent for one of those
const CONTEXTS_WITH_ASSIGNMENTS: string[] = [
    PRICING_SIMULATION_CONTEXT.GATEWAY,
    PRICING_SIMULATION_CONTEXT.ZONE,
    PRICING_SIMULATION_CONTEXT.SITE,
];

type SimulatedPeriod = {
    start: string;
    end: string;
    context_type: PRICING_SIMULATION_CONTEXT;
    context_id?: string;
    visitor_type?: VISITOR_TYPE;
};

/**
 * "Simulate pricing" - what a stay of a chosen period would be charged by the version of the pricing plan whose
 * dashboard the user came from. Read-only: the screen creates no stay and changes nothing.
 */
function PricingPlanSimulation() {
    const { formatMessage, formatNumber, locale } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: planData } = useGetPricingPlan({ planId, parkingId, config: { enabled: !!planId && !!parkingId } });

    const planName = planData?.translations?.find((translation) => translation.language === locale)?.name ?? '';

    const { data: linkedEntities } = useGetLinkedEntities({
        pricingPlanId: planId,
        parkingId,
        config: { enabled: !!planId && !!parkingId },
    });

    // Where the stay can be simulated: the tariff on its own, or any object it is used in - there the breakdown is a
    // whole stay, with the tariffs that take over priced by themselves
    const contextOptions = React.useMemo(() => {
        const translate = (translations?: { language: string; name: string }[]) =>
            translations?.find((translation) => translation.language === locale)?.name ?? '';

        const options: { value: string; label: string }[] = [
            {
                value: PRICING_SIMULATION_CONTEXT.PLAN as string,
                label: formatMessage(pricingPlanMessages.simulationContextPlan),
            },
        ];

        linkedEntities?.gateway_pricing_plans?.gateway_pricing_plans?.forEach((relation) =>
            options.push({
                value: `${PRICING_SIMULATION_CONTEXT.GATEWAY}:${relation.gateway.id}`,
                label: translate(relation.gateway.translations),
            }),
        );
        linkedEntities?.zone_pricing_plans?.zone_pricing_plans?.forEach((relation) =>
            options.push({
                value: `${PRICING_SIMULATION_CONTEXT.ZONE}:${relation.zone.id}`,
                label: translate(relation.zone.translations),
            }),
        );
        linkedEntities?.site_pricing_plans?.site_pricing_plans?.forEach((relation) =>
            options.push({
                value: `${PRICING_SIMULATION_CONTEXT.SITE}:${relation.site.id}`,
                label: translate(relation.site.translations),
            }),
        );
        linkedEntities?.membership_types?.membership_types?.forEach((membershipType) =>
            options.push({
                value: `${PRICING_SIMULATION_CONTEXT.MEMBERSHIP_TYPE}:${membershipType.id}`,
                label: translate(membershipType.translations),
            }),
        );

        // One object may hold several assignments of the tariff - it is offered once
        return options.filter((option, index) => options.findIndex((other) => other.value === option.value) === index);
    }, [formatMessage, linkedEntities, locale]);

    // The two per periods types tick by the tier the stay has reached, the fixed tick type by the tariff's own tick
    const isPerPeriods =
        planData?.calculation_type === CALCULATION_TYPE.PER_PERIODS_COMPOUND ||
        planData?.calculation_type === CALCULATION_TYPE.PER_PERIODS_TOTAL;

    const { data: tiersData } = useListPricingPlanTiers({
        planId,
        parkingId,
        config: { enabled: !!planId && !!parkingId && isPerPeriods },
    });

    // The shortest tick is the one that fills the breakdown fastest, so it decides how long a period still fits
    const tickMinutes = React.useMemo(() => {
        if (!isPerPeriods) return planData?.tick?.tick_minutes || null;

        const ticks = (tiersData?.results ?? [])
            .map((tier) => tier.tick_minutes)
            .filter((minutes) => minutes && minutes > 0);

        return ticks.length ? Math.min(...ticks) : null;
    }, [isPerPeriods, planData, tiersData]);

    const offeredPeriodMinutes = React.useMemo(() => {
        const week = DEFAULT_PERIOD_DAYS * MINUTES_IN_DAY;
        return tickMinutes ? Math.min(week, tickMinutes * MAX_OFFERED_TICKS) : week;
    }, [tickMinutes]);

    const defaultValues = React.useMemo(() => {
        const start = dayjs().startOf('hour');
        return {
            start: start.toISOString(),
            end: start.add(offeredPeriodMinutes, 'minute').toISOString(),
            context: PRICING_SIMULATION_CONTEXT.PLAN,
            // What an unknown visitor is recorded as, so it is the type a breakdown is most often wanted for
            visitor_type: VISITOR_TYPE.CAR,
        };
    }, [offeredPeriodMinutes]);

    const form = useForm<PricingSimulationFormSchemaValues>({
        resolver: zodResolver(pricingSimulationFormSchema),
        defaultValues,
    });

    // The period the breakdown below is for - set when the form is submitted, so nothing is requested before that
    const [period, setPeriod] = React.useState<SimulatedPeriod | null>(null);

    // Another plan is another screen: its breakdown and the objects it is used in are its own, so nothing of the
    // previous one is kept when the route moves from one plan to another without leaving the screen
    React.useEffect(() => {
        setPeriod(null);
        form.reset(defaultValues);
    }, [defaultValues, form, planId]);

    const { start } = form.watch();
    const previousStart = React.useRef(defaultValues.start);

    // Choosing a start prefills the end a week later - or sooner, when the tariff's tick cannot cover a week; the
    // user is free to change it afterwards
    React.useEffect(() => {
        if (!start || start === previousStart.current) return;

        previousStart.current = start;
        form.setValue('end', dayjs(start).add(offeredPeriodMinutes, 'minute').toISOString());
    }, [form, offeredPeriodMinutes, start]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(pricingPlanMessages.simulatePricingTitle));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data, isFetching, error } = useSimulatePricingPlan({
        planId,
        parkingId,
        start: period?.start ?? '',
        end: period?.end ?? '',
        context_type: period?.context_type,
        visitor_type: period?.visitor_type,
        context_id: period?.context_id,
        config: { enabled: !!planId && !!parkingId && !!period },
    });

    const formatCustomError = useFormatCustomError();

    // The period is refused by the backend (ending before it starts, too long for the tick of the plan) - its error
    // is shown under the form, where the person can correct it
    const errorMessage: string | null = React.useMemo(() => {
        const errors = (error as { response?: { data?: { errors?: SingleAttributeError[] } } } | null)?.response?.data
            ?.errors;

        return errors?.length ? errors.map((singleError) => formatCustomError(singleError)).join(' ') : null;
    }, [error, formatCustomError]);

    // When more than one tariff priced the stay, how much of it each one charged - the exception tariff of an object
    // is otherwise only visible row by row
    const tariffTotals = React.useMemo(() => {
        const totals = new Map<string, { name: string; amount: number }>();

        (data?.rows ?? []).forEach((row) => {
            if (!row.pricing_plan) return;

            const name =
                row.pricing_plan.translations.find((translation) => translation.language === locale)?.name ?? '';
            const current = totals.get(row.pricing_plan.id);

            totals.set(row.pricing_plan.id, { name, amount: (current?.amount ?? 0) + row.amount_with_tax });
        });

        return totals.size > 1 ? [...totals.values()] : [];
    }, [data, locale]);

    // The tariff itself and a membership type are not resolved through assignments - the type changes nothing there
    const hasAssignments = CONTEXTS_WITH_ASSIGNMENTS.includes(form.watch('context').split(':')[0]);

    const onSubmit = React.useCallback((values: PricingSimulationFormSchemaValues) => {
        const [context_type, context_id] = values.context.split(':');

        setPeriod({
            start: values.start,
            end: values.end,
            context_type: context_type as PRICING_SIMULATION_CONTEXT,
            context_id,
            visitor_type: CONTEXTS_WITH_ASSIGNMENTS.includes(context_type) ? values.visitor_type : undefined,
        });
    }, []);

    return (
        <>
            <BackButton />
            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockHead>
                        <SectionBlockTitle className="flex flex-wrap items-center gap-2 text-2xl">
                            {planName}
                            <PricingPlanBadge
                                badgeOptions={pricingCalculationBadgeOptions}
                                badgeIndexKey={planData?.calculation_type}
                            />
                        </SectionBlockTitle>
                        <SectionBlockSubtitle>
                            {formatMessage(pricingPlanMessages.simulatePricingSubtitle)}
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="flex flex-col items-start gap-4 md:flex-row md:items-end">
                        <FormField
                            control={form.control}
                            name="context"
                            render={({ field }) => (
                                <FormItem className="w-full md:w-72">
                                    <FormLabel required>
                                        {formatMessage(pricingPlanMessages.simulationContext)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {contextOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        {hasAssignments && (
                            <FormField
                                control={form.control}
                                name="visitor_type"
                                render={({ field }) => (
                                    <FormItem className="w-full md:w-56">
                                        <FormLabel required>
                                            {formatMessage(pricingPlanMessages.simulationVisitorType)}
                                        </FormLabel>
                                        <FormControlStack>
                                            <FormControl>
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {Object.values(VISITOR_TYPE).map((visitorType) => (
                                                            <SelectItem key={visitorType} value={visitorType}>
                                                                {formatMessage(
                                                                    visitorTypeBadgeOptions[visitorType].label,
                                                                )}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormControlStack>
                                    </FormItem>
                                )}
                            />
                        )}
                        <FormField
                            control={form.control}
                            name="start"
                            render={({ field }) => (
                                <FormItem className="w-full md:w-64">
                                    <FormLabel required>{formatMessage(pricingPlanMessages.periodStart)}</FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <DatePickerWithTime dateISO={field.value} onChange={field.onChange} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="end"
                            render={({ field }) => (
                                <FormItem className="w-full md:w-64">
                                    <FormLabel required>{formatMessage(pricingPlanMessages.periodEnd)}</FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <DatePickerWithTime
                                                dateISO={field.value}
                                                onChange={field.onChange}
                                                disabledDateRange={{ before: dayjs(start).startOf('day').toDate() }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <Button
                            type="submit"
                            disabled={isFetching}
                            // A form control grows to the height of its padded wrapper (42px) and the wrapper
                            // reaches 4px lower than the control - the button matches both, so that it lines up
                            // with the two date pickers instead of hanging below them
                            className="w-full md:mb-1 md:h-[42px] md:w-auto"
                        >
                            {isFetching && (
                                <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                            )}
                            {formatMessage(pricingPlanMessages.simulate)}
                        </Button>
                    </div>
                </Form>
                {errorMessage && <p className="text-destructive text-sm">{errorMessage}</p>}
                {data && !errorMessage && (
                    <div className="text-muted-foreground flex flex-col gap-1 text-sm">
                        <span>
                            {formatMessage(pricingPlanMessages.simulationTotalWithTax)}:{' '}
                            <span className="text-foreground font-medium">
                                {formatNumber(data.total_with_tax / 100, {
                                    style: 'currency',
                                    currency: data.currency,
                                })}
                            </span>
                        </span>
                        <span>
                            {formatMessage(pricingPlanMessages.simulationTotalWithoutTax)}:{' '}
                            <span className="text-foreground font-medium">
                                {formatNumber(data.total_amount / 100, {
                                    style: 'currency',
                                    currency: data.currency,
                                })}
                            </span>
                        </span>
                        <span>
                            {formatMessage(pricingPlanMessages.simulationTotalTax)}:{' '}
                            <span className="text-foreground font-medium">
                                {formatNumber(data.total_tax_amount / 100, {
                                    style: 'currency',
                                    currency: data.currency,
                                })}
                            </span>
                        </span>
                        {tariffTotals.length > 0 && (
                            <span className="flex flex-wrap gap-x-4 gap-y-1">
                                {tariffTotals.map((tariff) => (
                                    <span key={tariff.name}>
                                        {tariff.name}:{' '}
                                        <span className="text-foreground font-medium">
                                            {formatNumber(tariff.amount / 100, {
                                                style: 'currency',
                                                currency: data.currency,
                                            })}
                                        </span>
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                )}
            </SectionBlock>
            {period && !errorMessage && (
                <PricingSimulationTable simulation={data} isLoading={isFetching} simulatedPlanId={planId} />
            )}
        </>
    );
}

export default PricingPlanSimulation;
